import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useMatches, useNavigate } from '@remix-run/react';

import { COMMUNITY_ROUTE_IDS, COMMUNITY_ROUTES } from '~/constants/community';
import type { CommunitySpace } from '~/interfaces/community';
import toast from '~/utils/toast';

import { useSpaceAccess } from '../../_hooks/useSpaceAccess';
import { useUpgradeHandoff } from '../../_hooks/useUpgradeHandoff';

export interface ChannelUpgradePurchase {
  space: CommunitySpace;
  unlockedChannels: Pick<CommunitySpace, 'id' | 'name'>[];
}

export interface ChannelUpgradeHandoff {
  /** The purchase a modal is waiting on, so reopening its channel resumes it and a different one starts at its plans. */
  awaiting: ChannelUpgradePurchase | null;
  markAwaitingAccess: (purchase: ChannelUpgradePurchase) => void;
  onAccessGranted: () => void;
}

export const useChannelUpgradeHandoff = (): ChannelUpgradeHandoff => {
  const { t } = useTranslation();
  const { canAccessSpace } = useSpaceAccess();
  const navigate = useNavigate();
  const matches = useMatches();

  const isGranted = useCallback(({ space }: ChannelUpgradePurchase) => canAccessSpace(space), [canAccessSpace]);

  const onGranted = useCallback(
    ({ space }: ChannelUpgradePurchase) => {
      toast.success(t('community.upgrade_modal.channel_unlocked', { channelName: space.name }));

      // A post detail unlocks in place, since leaving would drop the post they were reading.
      if (!matches.some((match) => match.id === COMMUNITY_ROUTE_IDS.post)) {
        navigate(COMMUNITY_ROUTES.spaces(space.slug));
      }
    },
    [t, matches, navigate],
  );

  return useUpgradeHandoff<ChannelUpgradePurchase>({ modalType: 'community_channel_upgrade', isGranted, onGranted });
};
